import React, { useEffect, useRef, useState } from "react";
import mapboxGeocoding from "@mapbox/mapbox-sdk/services/geocoding";
import InputField from "components/fields/InputField";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const MapboxAddressInput = ({
  label,
  id,
  value,
  onResult,
  required,
  onClearInput,
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const geocodingClient = mapboxGeocoding({ accessToken: MAPBOX_TOKEN });

  // Fetch suggestions when query changes
  useEffect(() => {
    if (!query || !isFocused) return;

    const controller = new AbortController();
    geocodingClient
      .forwardGeocode({
        query,
        autocomplete: true,
        limit: 5,
      })
      .send()
      .then((response) => {
        const results = response.body.features;
        setSuggestions(results);
      })
      .catch(() => setSuggestions([]));

    return () => controller.abort();
  }, [query, isFocused]);

  const handleSelect = (place) => {
    const context = place?.context || [];
    const getContextText = (idPrefix) =>
      context?.find((c) => c?.id?.startsWith(idPrefix))?.text || "";

    const selectedData = {
      address: place?.place_name,
      state: getContextText("region"),
      // city: place?.text || "",
      city: getContextText("place"),
      street: place?.text || "",
      // street: getContextText("place"),
      zipCode: getContextText("postcode"),
      lat: place?.center[1],
      lng: place?.center[0],
    };

    onResult(selectedData);

    // Close dropdown and set selected value
    setQuery(place.place_name);
    setSuggestions([]);
    setIsFocused(false);
    inputRef.current.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      setActiveIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    }
  };

  useEffect(() => {
    if (value) {
      setQuery(value);
    }
  }, [value]);

  return (
    <div className="flex flex-col pt-4">
      <label
        htmlFor={id}
        className="text-sm text-navy-700 dark:text-white ml-2 mb-2"
      >
        {label} {required && "*"}
      </label>

      <div className="relative">
        <InputField
          type="text"
          id={id}
          ref={inputRef}
          // className="border px-3 py-2 rounded-md text-sm w-full min-h-[44px] relative"
          placeholder="Search address"
          value={query}
          onChange={(e) => {
            const inputValue = e.target.value;
            setQuery(inputValue);
            if (inputValue === "" && onClearInput) {
              onClearInput(); // Clear data only when input is empty
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTimeout(() => {
              setSuggestions([]);
              setIsFocused(false);
            }, 150);
          }}
          autoComplete="off"
        />

        {isFocused && suggestions.length > 0 && (
          // <ul className="absolute top-1 z-10 bg-white border rounded-md mt-10 shadow-md w-full max-w-[500px]">
          <ul
            className="absolute top-10 z-10 bg-white border rounded-md shadow-md w-full max-w-[500px] 
                 dark:bg-navy-400 dark:text-white dark:border-gray-700"
          >
            {suggestions?.map((s, i) => (
              <li
                key={s.id}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  i === activeIndex ? "bg-blue-100" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent blur before select
                  handleSelect(s);
                }}
              >
                {s.place_name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MapboxAddressInput;
